import { fetchAuthSession } from "@aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";
import { tokenManager } from "./tokenManager";
import { requestDeduplicator } from "./requestDeduplicator";

// Constants
const MAX_RETRIES = 3;
const RATE_LIMIT_WINDOW = 1000; // 1 second
const RATE_LIMIT_REQUESTS = 10;
const ALLOWED_DOMAINS = [
  ".execute-api.us-east-2.amazonaws.com",
  // Add other allowed domains here if needed
];

// Helper to validate env vars with fallback
const validateEnvVar = (name, value, fallback = null) => {
  if (!value && !fallback) {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        "Please check your .env file and ensure all required variables are set."
    );
  }

  // Use fallback if value is not available
  const finalValue = value || fallback;

  // Remove trailing slashes
  const cleanValue = finalValue.replace(/\/+$/, "");

  // Validate URL format
  try {
    new URL(cleanValue);
  } catch (e) {
    throw new Error(`Invalid URL in ${name}: ${cleanValue}`);
  }

  // Ensure HTTPS in production
  if (
    process.env.NODE_ENV === "production" &&
    !cleanValue.startsWith("https://")
  ) {
    throw new Error(`${name} must use HTTPS in production`);
  }

  return cleanValue;
};

// Sanitize URL to prevent injection
const sanitizeUrl = (url) => {
  try {
    const parsed = new URL(url);
    // Check against allowed domains
    if (!ALLOWED_DOMAINS.some((domain) => parsed.hostname.endsWith(domain))) {
      throw new Error("Invalid API domain");
    }
    // Normalize URL
    return parsed.toString();
  } catch (e) {
    throw new Error("Invalid URL format");
  }
};

// Token bucket rate limiter
const rateLimiter = (() => {
  let tokens = RATE_LIMIT_REQUESTS;
  let lastRefill = Date.now();

  return {
    tryAcquire: () => {
      const now = Date.now();
      const timePassed = now - lastRefill;
      // Refill tokens based on time passed
      tokens = Math.min(
        RATE_LIMIT_REQUESTS,
        tokens + (timePassed / RATE_LIMIT_WINDOW) * RATE_LIMIT_REQUESTS
      );
      lastRefill = now;

      if (tokens < 1) return false;
      tokens--;
      return true;
    },
  };
})();

// Validate and export your various API endpoints
export const API_BASE = validateEnvVar(
  "REACT_APP_API_BASE",
  process.env.REACT_APP_API_BASE
);
export const AUTH_API = validateEnvVar(
  "REACT_APP_AUTH_API",
  process.env.REACT_APP_AUTH_API
);
export const ROUTINES_API = validateEnvVar(
  "REACT_APP_ROUTINES_API",
  process.env.REACT_APP_ROUTINES_API
);
export const ROSTER_API = validateEnvVar(
  "REACT_APP_ROSTER_API",
  process.env.REACT_APP_ROSTER_API
);
export const JUDGES_API = validateEnvVar(
  "REACT_APP_JUDGES_API",
  process.env.REACT_APP_JUDGES_API
);
export const EVENTS_API = validateEnvVar(
  "REACT_APP_EVENTS_API",
  process.env.REACT_APP_EVENTS_API
);
export const CRITERIA_API = validateEnvVar(
  "REACT_APP_CRITERIA_API",
  process.env.REACT_APP_CRITERIA_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/criteria"
);
export const AWARDS_API = validateEnvVar(
  "REACT_APP_AWARDS_API",
  process.env.REACT_APP_AWARDS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/awards"
);
export const PHOTOS_API = validateEnvVar(
  "REACT_APP_PHOTOS_API",
  process.env.REACT_APP_PHOTOS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/photos"
);
export const USERS_API = validateEnvVar(
  "REACT_APP_USERS_API",
  process.env.REACT_APP_USERS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/users"
);
export const REGISTRATIONS_API = validateEnvVar(
  "REACT_APP_REGISTRATIONS_API",
  process.env.REACT_APP_REGISTRATIONS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/registrations"
);
export const COMPANY_SETTINGS_API = validateEnvVar(
  "REACT_APP_COMPANY_SETTINGS_API",
  process.env.REACT_APP_COMPANY_SETTINGS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/company-settings"
);
export const PRICE_SETTINGS_API = validateEnvVar(
  "REACT_APP_PRICE_SETTINGS_API",
  process.env.REACT_APP_PRICE_SETTINGS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/pricing-settings"
);
export const REGISTRATION_RULES_API = validateEnvVar(
  "REACT_APP_REGISTRATION_RULES_API",
  process.env.REACT_APP_REGISTRATION_RULES_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/registration-rules"
);
export const SEASON_EVENTS_API = validateEnvVar(
  "REACT_APP_SEASON_EVENTS_API",
  process.env.REACT_APP_SEASON_EVENTS_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/season-events"
);
export const STAGES_API = validateEnvVar(
  "REACT_APP_STAGES_API",
  process.env.REACT_APP_STAGES_API,
  "https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/stages"
);

// Add the user-specific endpoint
export const SEASON_EVENTS_USER_API = `${SEASON_EVENTS_API}?mode=user`;

// Get current auth token using centralized token manager
const getAuthToken = async () => {
  try {
    const token = await tokenManager.getToken();

    if (!token) {
      throw new Error("No valid authentication token found");
    }

    return token;
  } catch (error) {
    throw new Error("Failed to get authentication token");
  }
};

// Internal fetch function for use with deduplicator
const _fetchWithAuth = async (url, options = {}) => {
  try {
    // Get the authentication token using the existing getAuthToken function
    // instead of Auth.currentSession()
    const token = await getAuthToken();

    if (!token) {
      throw new Error('No authentication token available. Please log in again.');
    }

    // Prepare headers with authentication
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    // Process request body if present
    let processedOptions = { ...options, headers };
    
    if (options.body) {
      // If the body is an object (not already a string), stringify it normally
      if (typeof options.body === 'object') {
        processedOptions.body = JSON.stringify(options.body);
      } else {
        // If it's already a string, use it as is
        processedOptions.body = options.body;
      }
    }

    // Handle abort signals - combine external signal with timeout
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 30000); // 30 second timeout
    
    // If an external signal is provided, combine it with the timeout signal
    if (options.signal) {
      const combinedController = new AbortController();
      
      // Abort if either the external signal or timeout signal is triggered
      const abortHandler = () => combinedController.abort();
      options.signal.addEventListener('abort', abortHandler);
      timeoutController.signal.addEventListener('abort', abortHandler);
      
      processedOptions.signal = combinedController.signal;
    } else {
      processedOptions.signal = timeoutController.signal;
    }

    // Suppress logging for expected Stripe Connect requests that commonly fail
    const isStripeConnectRequest = url.includes('/connect/account-status/');
    if (!isStripeConnectRequest) {
      console.log(`API Request: ${options.method || 'GET'} ${url}`);
    }
    
    try {
      // Make the API request
      const response = await fetch(url, processedOptions);
      clearTimeout(timeoutId); // Clear the timeout if request completes
      
      // Handle errors
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: 'Unknown error' };
        }

        const error = new Error(errorData.message || `HTTP error ${response.status}`);
        error.status = response.status;
        error.responseData = errorData;

        // Handle specific error cases
        if (response.status === 401) {
          error.message = 'Your session has expired. Please log in again.';
          // Clear cached token on auth failure
          tokenManager.clearToken();
        } else if (response.status === 403) {
          error.message = 'You do not have permission to perform this action.';
        } else if (response.status === 404) {
          error.message = 'The requested resource was not found.';
        } else if (response.status >= 500) {
          error.message = 'A server error occurred. Please try again later.';
        }

        throw error;
      }

      // Parse the response - handle 204 No Content responses
      let data = null;
      if (response.status !== 204) {
        const contentLength = response.headers.get('content-length');
        const contentType = response.headers.get('content-type');
        
        // Only try to parse JSON if there's content and it's JSON
        if (contentLength !== '0' && contentType?.includes('application/json')) {
          try {
            data = await response.json();
          } catch (jsonError) {
            // If JSON parsing fails but response was successful, return empty object
            console.warn('Failed to parse JSON response, but request was successful');
            data = {};
          }
        } else {
          data = {};
        }
      } else {
        // 204 No Content - return empty object
        data = {};
      }
      
      console.log(`API Response: ${options.method || 'GET'} ${url} - Success`);
      return data;
    } catch (fetchError) {
      clearTimeout(timeoutId); // Clear the timeout
      
      // Handle abort errors (timeouts)
      if (fetchError.name === 'AbortError') {
        const timeoutError = new Error('Request timed out. Please check your connection and try again.');
        timeoutError.status = 408; // Request Timeout
        throw timeoutError;
      }
      
      throw fetchError;
    }
  } catch (error) {
    // Check if this is an expected Stripe Connect error that should be suppressed
    const isStripeConnectError = url.includes('/connect/account-status/') && 
                                (error.status === 400 || error.status === 404);
    
    // Log the error for debugging (suppress expected Stripe errors)
    if (!isStripeConnectError) {
      console.error('API request failed:', {
        url,
        method: options.method || 'GET',
        error: error.message,
        status: error.status,
        responseData: error.responseData
      });
    }

    // Rethrow the error with additional context
    if (!error.status) {
      error.message = `Network error: ${error.message}`;
    }
    throw error;
  }
};

// Main fetch utility with auth and deduplication
export const fetchWithAuth = async (url, options = {}) => {
  // Use request deduplicator to prevent concurrent identical requests
  return await requestDeduplicator.makeRequest(url, options, _fetchWithAuth);
};

// Optimized uploadToS3 function with fast modern fetch
export const uploadToS3 = async (url, file, onProgress = () => {}) => {
  const startTime = Date.now();
  
  try {
    // For small files (< 1MB), skip progress tracking for speed
    if (file.size < 1024 * 1024) {
      onProgress(50); // Show some progress immediately
      
      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }

      const uploadTime = (Date.now() - startTime) / 1000;
      onProgress(100); // Complete
      
      return {
        success: true,
        uploadTime,
        status: response.status,
      };
    }

    // For larger files, use XMLHttpRequest with progress tracking
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();

        // Set up progress listener with debouncing for smoother updates
        let lastProgressUpdate = 0;
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const now = Date.now();
            // Only update progress at most every 100ms to avoid too many rerenders
            if (now - lastProgressUpdate > 100) {
              const percentComplete = Math.round(
                (event.loaded / event.total) * 100
              );
              onProgress(percentComplete);
              lastProgressUpdate = now;
            }
          }
        });

        // Handle successful completion
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const uploadTime = (Date.now() - startTime) / 1000;

            // Always ensure 100% is shown at the end
            onProgress(100);

            // Resolve immediately - modern S3 doesn't need consistency delays
            resolve({
              success: true,
              uploadTime,
              status: xhr.status,
            });
          } else {
            reject({
              status: xhr.status,
              statusText: xhr.statusText,
              message: `S3 upload failed with status ${xhr.status}`,
            });
          }
        };

        // Handle errors
        xhr.onerror = () => {
          reject({
            status: xhr.status,
            statusText: xhr.statusText,
            message: "Network error occurred during upload",
          });
        };

        // Handle timeouts
        xhr.ontimeout = () => {
          reject({
            status: 408,
            statusText: "Request Timeout",
            message: "Upload timed out",
          });
        };

        // Handle abort
        xhr.onabort = () => {
          reject({
            status: 0,
            statusText: "Aborted",
            message: "Upload was aborted",
          });
        };

        // Open and send the request - DO NOT modify the URL
        xhr.open("PUT", url, true);
        xhr.setRequestHeader("Content-Type", file.type);

        // Set timeout to 5 minutes for larger files
        xhr.timeout = 300000; // 5 minutes in milliseconds

        xhr.send(file);
      } catch (error) {
        reject(error);
      }
    });
  } catch (error) {
    throw error;
  }
};

// Add a new helper function to convert file URLs to public URLs
// This can be used to migrate existing URLs in your database
export const getPublicUrlFromPresignedUrl = (presignedUrl) => {
  try {
    // Extract the bucket and key from the presigned URL
    const url = new URL(presignedUrl);
    const bucketName = url.hostname.split(".")[0];
    const region = url.hostname.split(".")[2]; // usually 'us-east-2'

    // The key is everything after the first slash in the pathname
    let key = url.pathname.substring(1);

    // Construct the public URL
    return `https://${bucketName}.s3.${region}.amazonaws.com/${key}`;
  } catch (error) {
    console.error("Error converting to public URL:", error);
    return null;
  }
};

// Add a new helper function to extract the key from a presigned URL
export const getKeyFromPresignedUrl = (presignedUrl) => {
  try {
    const url = new URL(presignedUrl);
    // The key is everything after the first slash in the pathname
    return url.pathname.substring(1);
  } catch (error) {
    console.error("Error extracting key from URL:", error);
    return null;
  }
};

// Add a function to refresh expired URLs
export const refreshExpiredUrl = async (key) => {
  try {
    if (!key) return null;

    const response = await fetchWithAuth(
      `${PHOTOS_API}?key=${encodeURIComponent(key)}`
    );

    // If the response contains a publicUrl, use that
    if (response && response.publicUrl) {
      return response.publicUrl;
    }

    // Otherwise, fall back to the presigned URL
    return response?.url || null;
  } catch (error) {
    console.error("Error refreshing URL:", error);
    return null;
  }
};

// Consolidated API methods
export const api = {
  // Auth
  checkAuth: () => fetchWithAuth(`${AUTH_API}/validate`),

  // Routines
  getRoutines: () => fetchWithAuth(ROUTINES_API),
  getRoutineById: (id) => fetchWithAuth(`${ROUTINES_API}/${id}`),
  createRoutine: (data) =>
    fetchWithAuth(ROUTINES_API, {
      method: "POST",
      body: data,
    }),
  updateRoutine: (id, data) =>
    fetchWithAuth(`${ROUTINES_API}/${id}`, {
      method: "PUT",
      body: data,
    }),
  deleteRoutine: (id) =>
    fetchWithAuth(`${ROUTINES_API}/${id}`, {
      method: "DELETE",
    }),

  // Roster
  getRoster: () => fetchWithAuth(ROSTER_API),
  getRosterMember: (id) => fetchWithAuth(`${ROSTER_API}/${id}`),
  createRosterMember: (data) =>
    fetchWithAuth(ROSTER_API, {
      method: "POST",
      body: data,
    }),
  updateRosterMember: (id, data) =>
    fetchWithAuth(`${ROSTER_API}/${id}`, {
      method: "PUT",
      body: data,
    }),
  deleteRosterMember: (id) =>
    fetchWithAuth(`${ROSTER_API}/${id}`, {
      method: "DELETE",
    }),
  bulkCreateRosterMembers: (members) =>
    fetchWithAuth(`${ROSTER_API}/bulk`, {
      method: "POST",
      body: JSON.stringify({ members }),
    }),

  // Events
  getEvents: () => fetchWithAuth(EVENTS_API),

  // Users
  getUsers: () => fetchWithAuth(USERS_API),
  getUserById: (userId) => fetchWithAuth(`${USERS_API}/${userId}`),

  // Direct DynamoDB access for Users (for development/testing)
  getUserFromDynamoDB: async (userId) => {
    try {
      // This is a direct call to the DynamoDB API endpoint
      const response = await fetchWithAuth(
        `${API_BASE}/users/direct/${userId}`
      );
      return response;
    } catch (error) {
      console.error("Error fetching user from DynamoDB:", error);
      return null;
    }
  },

  // Function to get user directly from multiple sources
  getUserDirectly: async (userId) => {
    console.log(`Attempting to get user with ID: ${userId} directly`);

    // Method 1: Try direct DynamoDB query
    try {
      console.log(`Querying DynamoDB for user: ${userId}`);

      // Use the AWS SDK to query DynamoDB directly
      const dynamoEndpoint = `${API_BASE}/users/dynamo/${userId}`;
      console.log(`DynamoDB endpoint: ${dynamoEndpoint}`);

      const response = await fetchWithAuth(dynamoEndpoint);
      console.log(`DynamoDB response for ${userId}:`, response);

      if (response && (response.firstName || response.lastName)) {
        console.log(
          `Successfully retrieved user from DynamoDB: ${response.firstName} ${response.lastName}`
        );
        return response;
      }
    } catch (err) {
      console.error(`DynamoDB query failed for ${userId}:`, err);
    }

    // Method 2: Try standard API endpoint
    try {
      console.log(`Trying standard API for user: ${userId}`);
      const userResponse = await fetchWithAuth(`${USERS_API}/${userId}`);

      if (userResponse && (userResponse.firstName || userResponse.lastName)) {
        console.log(
          `Successfully retrieved user from standard API: ${userResponse.firstName} ${userResponse.lastName}`
        );
        return userResponse;
      }
    } catch (err) {
      console.error(`Standard API method failed for ${userId}:`, err);
    }

    // Method 3: Try direct fetch to the users API with query parameter
    try {
      console.log(`Trying direct API with query parameter for user: ${userId}`);
      const queryResponse = await fetchWithAuth(
        `${USERS_API}?userId=${userId}`
      );

      if (
        queryResponse &&
        Array.isArray(queryResponse) &&
        queryResponse.length > 0
      ) {
        const user = queryResponse[0];
        console.log(
          `Successfully retrieved user from query API: ${user.firstName} ${user.lastName}`
        );
        return user;
      }
    } catch (err) {
      console.error(`Query parameter method failed for ${userId}:`, err);
    }

    // Method 4: Try direct fetch to the users API with POST
    try {
      console.log(`Trying direct API with POST for user: ${userId}`);
      const postResponse = await fetchWithAuth(`${USERS_API}/getUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      });

      if (postResponse && (postResponse.firstName || postResponse.lastName)) {
        console.log(
          `Successfully retrieved user from POST API: ${postResponse.firstName} ${postResponse.lastName}`
        );
        return postResponse;
      }
    } catch (err) {
      console.error(`POST method failed for ${userId}:`, err);
    }

    // Method 5: Try a direct scan of the Users table
    try {
      console.log(`Trying direct scan of Users table for user: ${userId}`);
      const scanResponse = await fetchWithAuth(`${API_BASE}/users/scan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filterExpression: "userId = :userId",
          expressionAttributeValues: {
            ":userId": userId,
          },
        }),
      });

      if (
        scanResponse &&
        Array.isArray(scanResponse) &&
        scanResponse.length > 0
      ) {
        const user = scanResponse[0];
        console.log(
          `Successfully retrieved user from scan: ${user.firstName} ${user.lastName}`
        );
        return user;
      }
    } catch (err) {
      console.error(`Scan method failed for ${userId}:`, err);
    }

    console.error(`All methods failed to retrieve user ${userId}`);
    return null;
  },

  // Judges
  getJudges: () => fetchWithAuth(JUDGES_API),

  // Awards
  getAwards: () => fetchWithAuth(AWARDS_API),

  // Criteria
  getCriteria: () => fetchWithAuth(CRITERIA_API),

  // Photos
  getPhotos: () => fetchWithAuth(PHOTOS_API),

  // Event Payment Settings
  updateEventPaymentSettings: async (eventId, settings) => {
    try {
      if (!eventId) {
        throw new Error("Event ID is required");
      }

      const response = await fetchWithAuth(
        `${EVENTS_API}/${eventId}/payment-settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentSettings: settings,
          }),
        }
      );

      return response;
    } catch (error) {
      console.error("Error updating event payment settings:", error);
      throw error;
    }
  },

  // Stages Management
  getStageConfigurations: () => fetchWithAuth(STAGES_API),
  
  createStageConfiguration: (stageConfig) =>
    fetchWithAuth(STAGES_API, {
      method: "POST",
      body: stageConfig,
    }),
  
  updateStageConfiguration: (eventName, stageConfig) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}`, {
      method: "PUT",
      body: stageConfig,
    }),
  
  deleteStageConfiguration: (eventName) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}`, {
      method: "DELETE",
    }),
  
  getStageSchedule: (eventName, stageId) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}/${stageId}`),
  
  createStageSchedule: (eventName, stageId, routines) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}/${stageId}`, {
      method: "POST",
      body: { routines },
    }),
  
  updateStageSchedule: (eventName, stageId, routines) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}/${stageId}`, {
      method: "PUT",
      body: { routines },
    }),
  
  deleteStageSchedule: (eventName, stageId) =>
    fetchWithAuth(`${STAGES_API}/${encodeURIComponent(eventName)}/${stageId}`, {
      method: "DELETE",
    }),
};

// Export default API object with all methods
export default api;
